import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { combineLatest, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

import { addVehicleBindQuery, addVehicleInfoQuery } from '@amfam/policy/add-vehicle/data-access';
import { VehicleOwnership } from '@amfam/policy/models';
import { vehicleQuery } from '@amfam/policy/vehicle/data-access';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import { LoadingSpinnerService, Option } from '@amfam/ui-kit';

import * as VehicleInfoActions from '../+state/add-vehicle-feature.actions';
import { addVehicleFeatureQuery } from '../+state/add-vehicle-feature.selectors';
import { VehicleUnderwritingPartnerQuestionsModels } from '../models/vehicle-info.model';

@Component({
  selector: 'ds-vehicle-underwriting-questions',
  templateUrl: './vehicle-underwriting-questions.component.html',
  styleUrls: ['../add-vehicle-container/add-vehicle-container.component.scss'],
  standalone: false
})
export class VehicleUnderwritingQuestionsComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() cancelEvent = new EventEmitter<boolean>();

  public questions: Option[] = [];
  public vehicleRiskDescription$ = this.store.select(vehicleQuery.selectNewRiskDescription);
  public underwritingFormGroup: UntypedFormGroup;
  isPartner = false;

  private afiQuestions: Option[] = [
    {
      text: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionOwnVehicle')['key'],
      helpText: undefined,
      id: '1',
      code: VehicleOwnership.OWN
    },
    {
      text: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionIHaveALoan')['key'],
      helpText: undefined,
      id: '2',
      code: VehicleOwnership.LOAN
    },
    {
      text: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionIHaveALease')['key'],
      helpText: undefined,
      id: '3',
      code: VehicleOwnership.LEASE
    }
  ];

  private partnerQuestions: Option[] = [
    {
      text: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionIsOwner')['key'],
      helpText: [
        {
          text: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionIsOwner')['helpText']
        }
      ],
      id: '1'
    },
    {
      text: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionIsBusinessUse')['key'],
      helpText: [
        {
          text: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionIsBusinessUse')[
            'helpText'
          ]
        }
      ],
      id: '2'
    },
    {
      text: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionHasOnlyLoan')['key'],
      helpText: [
        {
          text: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionHasOnlyLoan')[
            'helpText'
          ]
        }
      ],
      id: '3'
    },
    {
      text: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionHasOnlyLease')['key'],
      helpText: [
        {
          text: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionHasOnlyLease')[
            'helpText'
          ]
        }
      ],
      id: '4'
    },
    {
      text: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionIsDamaged')['key'],
      helpText: [
        {
          text: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionIsDamaged')[
            'helpText'
          ]
        }
      ],
      id: '5'
    },
    {
      text: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionKitCarReplica')['key'],
      helpText: [
        {
          text: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionKitCarReplica')[
            'helpText'
          ]
        }
      ],
      id: '6'
    },
    {
      text: this.copyService.getCopy('policy.newVehicleUnderWritingQuestionNoneOfThese'),
      helpText: [{ text: '' }],
      id: '7'
    }
  ];

  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private store: Store,
    private formBuilder: UntypedFormBuilder,
    private copyService: CopyService,
    private spinnerService: LoadingSpinnerService
  ) {}

  ngOnInit(): void {
    this.spinnerService.stop();
    this.buildForm();
    this.handleError();
  }

  ngAfterViewInit() {
    this.store.dispatch(
      VehicleInfoActions.addVehicleSendAnalytics({
        payload: { pageAnalyticName: 'UWAndTPIDetails' }
      })
    );
  }

  handleCheckBoxInteraction(updatedItemId: string) {
    const noneOptionId = this.questions.length + '';
    if (updatedItemId === noneOptionId) {
      for (let i = 0; i < this.underwritingFormGroup.controls['option']['controls'].length; i++) {
        if (i < this.questions.length - 1) {
          this.underwritingFormGroup.controls['option']['controls'][i].setValue('');
        }
      }
    }
    if (updatedItemId !== noneOptionId) {
      this.underwritingFormGroup.controls['option']['controls'][this.questions.length - 1].setValue(
        ''
      );
    }
  }

  underwritingFormSubmit() {
    this.spinnerService.start({
      blockActions: true
    });

    const hasLoanOrLease =
      this.underwritingFormGroup?.value?.option?.code === VehicleOwnership.LOAN ||
      this.underwritingFormGroup?.value?.option?.code === VehicleOwnership.LEASE;

    this.store.dispatch(
      VehicleInfoActions.setVehicleUnderwritingQuestionsInfo({
        underwritingQuestions: {
          hasLoanOrLease: hasLoanOrLease
        }
      })
    );

    this.store.dispatch(VehicleInfoActions.addVehicleInitiate({ hasTPI: hasLoanOrLease }));
  }

  public validators(): boolean {
    let isValid = false;
    isValid = this.underwritingFormGroup.valid;
    return isValid;
  }

  goBackOneStep() {
    this.store.dispatch(fromRouterActions.Back());
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }

  private buildForm() {
    this.store
      .select(BrandSelectors.selectIsPartner)
      .pipe(take(1))
      .subscribe((isPartner: boolean) => {
        this.isPartner = isPartner;
        this.questions = this.afiQuestions;
        this.underwritingFormGroup = this.formBuilder.group(
          {
            option: ['', Validators.compose([Validators.required])]
          },
          {
            validators: null
          }
        );
      });
  }

  private handleError() {
    combineLatest([
      this.store.select(addVehicleInfoQuery.selectNewVehicleInitiateError),
      this.store.select(addVehicleBindQuery.selectVehicleANSNotificationError)
    ])
      .pipe(
        map(([initiateError, ansError]) => ({ initiateError, ansError })),
        takeUntil(this.stop$)
      )
      .subscribe(({ initiateError, ansError }) => {
        if (initiateError || ansError) {
          this.spinnerService.stop();
          this.store.dispatch(
            fromRouterActions.Go({
              path: ['policies/add-vehicle/agent-help']
            })
          );
        }
      });
  }
  private setupMultiFormControl(questions: Option[]): UntypedFormArray {
    const formControls = questions.map(() => new UntypedFormControl(false));

    return new UntypedFormArray(formControls);
  }
  private updateForm() {
    const formControls = this.underwritingFormGroup.controls['option']['controls'];
    this.store
      .select(addVehicleFeatureQuery.selectCurrentVehicleInfo)
      .pipe(take(1))
      .subscribe(vehicleInfo => {
        if (vehicleInfo) {
          const underwritingQuestionModel = <VehicleUnderwritingPartnerQuestionsModels>(
            vehicleInfo.underwritingQuestions
          );
          formControls[0].setValue(underwritingQuestionModel.hasOtherOrCoowner);
          formControls[1].setValue(underwritingQuestionModel.usedForBusiness);
          formControls[2].setValue(underwritingQuestionModel.hasLoan);
          formControls[3].setValue(underwritingQuestionModel.hasLease);
          formControls[4].setValue(underwritingQuestionModel.isDamaged);
          formControls[5].setValue(underwritingQuestionModel.kitCarReplica);
          formControls[6].setValue(underwritingQuestionModel.noneOfThese);
        }
      });
  }
}
